import * as THREE from 'three';

/**
 * 🌌 粒子加载器 - V2X道路3D可视化的魔法入场效果
 * =====================================================
 *
 * 🎭 【概念比喻】
 * 想象一下科幻电影中太空战舰的全息投影启动效果：
 * • 数千个发光粒子在空中飞舞，逐渐汇聚
 * • 最终形成一条完整的3D道路模型
 * • 就像《钢铁侠》中托尼·斯塔克设计图纸的呈现过程
 *
 * 🔬 【技术原理】
 * • 基于Three.js粒子系统（Points）实现
 * • 使用BufferGeometry存储大量粒子的位置、速度、大小数据
 * • 采用加法混合模式(AdditiveBlending)营造发光效果
 * • 通过数学插值实现粒子从随机分布到道路形状的变换
 *
 * 🎯 【应用场景】
 * • V2X道路数据加载时的视觉反馈
 * • 提升用户体验，减少等待焦虑
 * • 为专业交通管理系统增添科技感
 * • 可扩展为各种3D模型的粒子重组效果
 *
 * @example
 * ```typescript
 * // 创建粒子加载器
 * const loader = new ParticleLoader(container, {
 *   particleCount: 3000,     // 粒子数量
 *   particleColor: 0x00ff88, // 翠绿色粒子
 *   backgroundColor: 0x001122 // 深蓝色背景
 * });
 *
 * // 开始粒子动画
 * loader.start();
 *
 * // 模拟数据加载进度
 * for(let i = 0; i <= 100; i += 10) {
 *   await new Promise(resolve => setTimeout(resolve, 200));
 *   loader.updateProgress(i);
 * }
 *
 * // 完成加载，粒子汇聚成道路形状
 * await loader.complete();
 * ```
 */
export class ParticleLoader {
  // 🎬 Three.js核心组件 - 就像电影拍摄的基础设备
  private scene!: THREE.Scene; // 舞台场景
  private camera!: THREE.Camera; // 摄像机视角
  private renderer!: THREE.WebGLRenderer; // 渲染引擎

  // ✨ 粒子系统组件 - 构成魔法效果的基础元素
  private particles!: THREE.Points; // 粒子集合（就像星空中的星点）
  private particleGeometry!: THREE.BufferGeometry; // 几何体（存储粒子位置数据）
  private particleMaterial!: THREE.PointsMaterial; // 材质（定义粒子外观）

  // 🎮 控制与状态管理
  private container: HTMLElement; // DOM容器元素
  private animationId: number | null = null; // 动画循环ID（用于取消动画）
  private progress: number = 0; // 加载进度(0-100)
  private isComplete: boolean = false; // 是否完成标志
  private onCompleteCallback?: () => void; // 完成回调函数

  /**
   * 🏗️ 构造函数 - 建造粒子宇宙的蓝图
   *
   * 🎭 【比喻】就像搭建一个全息投影实验室：
   * • 选择实验台尺寸（容器）
   * • 设定粒子密度（粒子数量）
   * • 调配发光材料（颜色配置）
   *
   * @param container - 🖼️ HTML容器元素（实验台）
   * @param options - ⚙️ 配置选项对象
   * @param options.particleCount - 🌟 粒子数量，默认2000个（密度越高效果越细腻）
   * @param options.particleColor - 🎨 粒子颜色，默认0x667eea蓝紫色
   * @param options.backgroundColor - 🌌 背景颜色，默认0x0f0f23深蓝色
   */
  constructor(
    container: HTMLElement,
    options: {
      particleCount?: number;
      particleColor?: number;
      backgroundColor?: number;
    } = {}
  ) {
    this.container = container;

    // 📋 解构赋值提取配置参数（设置默认值）
    const {
      particleCount = 2000, // 🌟 2000个粒子提供良好的视觉密度
      particleColor = 0x667eea, // 🎨 蓝紫色营造科技感
      backgroundColor = 0x0f0f23, // 🌌 深蓝背景突出粒子亮度
    } = options;

    // 🔧 按序初始化各个组件（顺序很重要！）
    this.initScene(backgroundColor); // 1. 创建舞台
    this.initCamera(); // 2. 设置摄像机
    this.initRenderer(); // 3. 配置渲染器
    this.initParticles(particleCount, particleColor); // 4. 生成粒子系统
  }

  /**
   * 🎬 初始化场景 - 搭建3D世界的舞台
   *
   * 🎭 【比喻】就像在黑盒剧院中布置舞台：
   * • Scene是整个3D世界的容器
   * • background设置舞台背景色调
   * • 为后续添加灯光、物体做准备
   *
   * 🔬 【技术细节】
   * • THREE.Scene是Three.js的根容器对象
   * • THREE.Color将16进制颜色值转换为Three.js颜色对象
   * • scene.background设置渲染背景色
   *
   * @param backgroundColor - 🌌 背景颜色（16进制数值）
   */
  private initScene(backgroundColor: number): void {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(backgroundColor);
  }

  /**
   * 📷 初始化相机 - 设置观察世界的视角
   *
   * 🎭 【比喻】就像给摄影师配置专业相机：
   * • PerspectiveCamera模拟人眼透视效果
   * • FOV(75°)提供自然的视野范围
   * • 近平面(0.1)和远平面(1000)定义可见距离
   * • 相机位置决定观察角度
   *
   * 🔬 【技术参数解释】
   * • 75° FOV：接近人眼视野，不会太广角也不会太窄
   * • aspect：宽高比自适应容器尺寸，避免画面变形
   * • near(0.1)：太小会导致Z-fighting，太大会裁剪近物体
   * • far(1000)：足够包含整个粒子运动范围
   * • position.z=100：后退到合适距离观察粒子群
   *
   * @private
   */
  private initCamera(): void {
    this.camera = new THREE.PerspectiveCamera(
      75, // 🔍 视野角度(Field of View) - 75度提供自然透视
      this.container.clientWidth / this.container.clientHeight, // 📐 宽高比
      0.1, // 📏 近裁剪面 - 0.1单位距离
      1000 // 📏 远裁剪面 - 1000单位距离
    );
    this.camera.position.z = 100; // 🚶 摄像机后退到100单位距离
  }

  /**
   * 🖥️ 初始化渲染器 - 配置图像绘制引擎
   *
   * 🎭 【比喻】就像调试电影院的投影设备：
   * • WebGL渲染器负责将3D场景转换为2D图像
   * • antialias开启抗锯齿让边缘更平滑
   * • setPixelRatio适配高分辨率屏幕
   * • 将canvas画布插入DOM容器
   *
   * 🔬 【技术细节】
   * • WebGLRenderer基于GPU硬件加速，性能最佳
   * • antialias:true开启多重采样抗锯齿(MSAA)
   * • devicePixelRatio处理视网膜屏幕的像素密度
   * • appendChild将Three.js的canvas添加到页面
   *
   * ⚡ 【性能提示】
   * • 抗锯齿会消耗GPU性能，移动端可考虑关闭
   * • 高像素比会增加渲染负载，可设置上限值
   *
   * @private
   */
  private initRenderer(): void {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true, // 🔧 开启抗锯齿，让粒子边缘更平滑
    });

    // 📏 设置渲染尺寸（匹配容器大小）
    this.renderer.setSize(
      this.container.clientWidth,
      this.container.clientHeight
    );

    // 📱 适配高分辨率屏幕（如视网膜屏幕）
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // 🔗 将渲染画布添加到DOM容器
    this.container.appendChild(this.renderer.domElement);
  }

  /**
   * ✨ 初始化粒子系统 - 创造星辰大海的魔法
   *
   * 🎭 【比喻】就像天文学家在虚拟宇宙中播撒星辰：
   * • BufferGeometry是存储星辰坐标的星图
   * • 每个粒子都有位置、速度、大小属性
   * • 球面分布模拟粒子从四面八方汇聚
   * • PointsMaterial定义每颗"星辰"的发光效果
   *
   * 🔬 【数学原理】
   * • 球坐标系转换：theta(方位角)、phi(极角)、radius(半径)
   * • 球面均匀分布：Math.acos(Math.random()*2-1)确保极点不稀疏
   * • 向心速度：velocity = -position * 0.01，让粒子向原点汇聚
   * • 加法混合：让重叠粒子亮度叠加，产生发光效果
   *
   * 🎨 【视觉效果】
   * • AdditiveBlending：重叠区域变亮，模拟真实发光
   * • sizeAttenuation：距离衰减让远处粒子变小
   * • transparent+opacity：半透明效果增强梦幻感
   *
   * @param count - 🌟 粒子数量（建议1000-5000之间平衡效果与性能）
   * @param color - 🎨 粒子颜色（16进制颜色值）
   * @private
   */
  private initParticles(count: number, color: number): void {
    // 📊 创建几何体容器（用于存储所有粒子数据）
    this.particleGeometry = new THREE.BufferGeometry();

    // 🗃️ 预分配内存数组（提升性能，避免动态扩容）
    const positions = new Float32Array(count * 3); // 位置数据(x,y,z)
    const velocities = new Float32Array(count * 3); // 速度数据(vx,vy,vz)
    const sizes = new Float32Array(count); // 大小数据

    // 🎲 为每个粒子生成随机属性
    for (let i = 0; i < count; i++) {
      const i3 = i * 3; // 🔢 3D坐标索引偏移量

      // 🌐 球面坐标系生成均匀分布（避免极点聚集问题）
      const theta = Math.random() * Math.PI * 2; // 🧭 方位角[0, 2π]
      const phi = Math.acos(Math.random() * 2 - 1); // 🎯 极角，均匀分布
      const radius = Math.random() * 50 + 10; // 📏 半径[10, 60]

      // 📐 球坐标转换为笛卡尔坐标（x,y,z）
      positions[i3] = radius * Math.sin(phi) * Math.cos(theta); // x坐标
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta); // y坐标
      positions[i3 + 2] = radius * Math.cos(phi); // z坐标

      // 🏹 设置粒子向中心汇聚的速度向量
      // 速度方向指向原点，大小与距离成正比
      velocities[i3] = -positions[i3] * 0.01; // 向心速度x分量
      velocities[i3 + 1] = -positions[i3 + 1] * 0.01; // 向心速度y分量
      velocities[i3 + 2] = -positions[i3 + 2] * 0.01; // 向心速度z分量

      // 🔸 随机粒子大小[1, 3]，增加视觉层次感
      sizes[i] = Math.random() * 2 + 1;
    }

    // 🗂️ 将数据绑定到几何体属性
    this.particleGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3) // 每个顶点3个坐标值
    );
    this.particleGeometry.setAttribute(
      'velocity',
      new THREE.BufferAttribute(velocities, 3) // 自定义属性存储速度
    );
    this.particleGeometry.setAttribute(
      'size',
      new THREE.BufferAttribute(sizes, 1) // 每个顶点1个大小值
    );

    // 🎨 创建粒子材质（定义粒子外观）
    this.particleMaterial = new THREE.PointsMaterial({
      color: color, // 🌈 基础颜色
      size: 2, // 🔸 默认粒子大小
      sizeAttenuation: true, // 📏 启用距离衰减（远小近大）
      transparent: true, // 🔍 启用透明度
      opacity: 0.8, // 👻 半透明效果(80%不透明度)
      blending: THREE.AdditiveBlending, // ✨ 加法混合（重叠变亮）
    });

    // 🌌 创建粒子系统对象并添加到场景
    this.particles = new THREE.Points(
      this.particleGeometry,
      this.particleMaterial
    );
    this.scene.add(this.particles);
  }

  /**
   * 🚀 开始粒子动画 - 启动魔法表演
   *
   * 🎭 【比喻】就像按下全息投影的启动按钮：
   * • 粒子开始在3D空间中运动
   * • 动画循环持续更新每一帧
   * • 为后续的进度更新和形状变换做准备
   *
   * 🔬 【技术实现】
   * • 调用animate()方法启动requestAnimationFrame循环
   * • 动画循环会持续到complete()被调用
   * • 每帧更新粒子位置并重新渲染场景
   *
   * @public
   */
  public start(): void {
    this.animate();
  }

  /**
   * 📊 更新加载进度 - 控制粒子聚合程度
   *
   * 🎭 【比喻】就像调节磁场强度，让铁屑逐渐向磁铁聚拢：
   * • 进度越高，粒子向中心汇聚得越快
   * • 通过调节速度因子实现渐进式聚合效果
   * • 距离中心较远的粒子优先加速
   *
   * 🔬 【算法原理】
   * • convergenceFactor = progress/100 将进度映射到[0,1]
   * • 计算每个粒子到原点的距离
   * • 对距离>5的粒子增加向心速度
   * • 速度放大系数：1 + convergenceFactor * 0.1
   *
   * 💡 【设计思路】
   * • distance>5：避免中心粒子过度聚集
   * • 0.1系数：确保速度变化不会太剧烈
   * • 实时更新：每次调用立即生效
   *
   * @param progress - 📈 进度值(0-100)，会自动钳制到有效范围
   * @public
   */
  public updateProgress(progress: number): void {
    // 🔒 钳制进度值到[0, 100]范围，防止异常输入
    this.progress = Math.max(0, Math.min(100, progress));

    // 📊 获取粒子位置和速度数据引用（直接操作内存，高效）
    const positions = this.particleGeometry.attributes.position
      .array as Float32Array;
    const velocities = this.particleGeometry.attributes.velocity
      .array as Float32Array;

    // 📐 计算聚合因子[0,1]，控制粒子汇聚强度
    const convergenceFactor = this.progress / 100;

    // 🔄 遍历所有粒子，调整向心速度
    for (let i = 0; i < positions.length; i += 3) {
      // 📏 计算粒子到原点的欧几里得距离
      const distance = Math.sqrt(
        positions[i] * positions[i] + // x²
          positions[i + 1] * positions[i + 1] + // y²
          positions[i + 2] * positions[i + 2] // z²
      );

      // 🎯 只调整距离中心较远的粒子（避免中心过度拥挤）
      if (distance > 5) {
        // ⚡ 增加向心速度，让粒子加速向中心移动
        velocities[i] *= 1 + convergenceFactor * 0.1; // x方向加速
        velocities[i + 1] *= 1 + convergenceFactor * 0.1; // y方向加速
        velocities[i + 2] *= 1 + convergenceFactor * 0.1; // z方向加速
      }
    }
  }

  /**
   * 🏁 完成加载动画 - 粒子汇聚成道路奇迹
   *
   * 🎭 【比喻】就像魔法师的终极咒语，让漫天星辰汇聚成一条通天大道：
   * • 粒子停止随机运动，开始有序排列
   * • 从混沌的点云变换为规整的道路形状
   * • 就像《星际穿越》中五维空间的书架显现过程
   *
   * 🔬 【技术流程】
   * • 设置完成标志，停止常规粒子运动
   * • 调用createRoadShape()计算道路目标位置
   * • 启动形状变换动画
   * • 返回Promise以支持async/await模式
   *
   * 💡 【Promise设计】
   * • 异步操作让调用者能等待动画完成
   * • 回调函数确保在fadeOut后正确resolve
   * • 支持链式调用和现代异步编程模式
   *
   * @returns {Promise<void>} 🔮 动画完成的Promise，可用于await等待
   * @public
   *
   * @example
   * ```typescript
   * // 等待粒子汇聚完成
   * await loader.complete();
   * console.log('道路已成形，可以开始导航了！');
   *
   * // 或者使用回调方式
   * loader.complete().then(() => {
   *   console.log('粒子魔法完成！');
   * });
   * ```
   */
  public complete(): Promise<void> {
    return new Promise(resolve => {
      this.isComplete = true; // 🏁 标记动画进入完成阶段
      this.onCompleteCallback = resolve; // 🔗 保存Promise的resolve函数

      // 🛣️ 创建道路形状的粒子聚合效果
      this.createRoadShape();
    });
  }

  /**
   * 🛣️ 创建道路形状 - 将混沌粒子重组为有序道路
   *
   * 🎭 【比喻】就像城市规划师重新设计街道布局：
   * • 将散乱的粒子按照道路蓝图重新排列
   * • 每个粒子都有自己在道路中的专属位置
   * • 形成一条宽度适中、长度合理的3D道路
   *
   * 🔬 【算法设计】
   * • 使用线性插值(t)在道路长度上分布粒子
   * • roadWidth控制道路宽度，roadLength控制长度
   * • x轴：道路宽度内随机分布（模拟车道宽度）
   * • y轴：轻微高度变化（模拟路面起伏）
   * • z轴：沿道路长度线性分布（从-40到+40）
   *
   * 🛣️ **道路几何设计**：
   * • 宽度10单位：足够容纳多车道效果
   * • 长度80单位：提供足够的道路延伸感
   * • 居中布置：Z轴从-40到+40对称分布
   *
   * @private
   */
  private createRoadShape(): void {
    // 📊 获取当前粒子位置数据
    const positions = this.particleGeometry.attributes.position
      .array as Float32Array;

    // 🎯 创建目标位置数组（粒子最终应该到达的位置）
    const targetPositions = new Float32Array(positions.length);

    // 🛣️ 道路设计参数
    const roadWidth = 10; // 🛣️ 道路宽度（模拟双向四车道）
    const roadLength = 80; // 📏 道路总长度

    // 🔄 为每个粒子计算在道路中的目标位置
    for (let i = 0; i < positions.length; i += 3) {
      // 📊 计算当前粒子在总数中的位置比例[0,1]
      const t = i / 3 / (positions.length / 3);

      // 📐 分配粒子在道路中的3D坐标
      targetPositions[i] = (Math.random() - 0.5) * roadWidth; // X: 车道内随机分布
      targetPositions[i + 1] = (Math.random() - 0.5) * 2; // Y: 轻微高度变化
      targetPositions[i + 2] = t * roadLength - roadLength / 2; // Z: 沿道路均匀分布
    }

    // 🎬 启动形状变换动画
    this.animateToShape(targetPositions);
  }

  /**
   * 🎬 动画到指定形状 - 粒子变形的魔法过程
   *
   * 🎭 【比喻】就像看延时摄影中花朵绽放的过程：
   * • 每个粒子都有自己的起点和终点
   * • 通过数学插值让粒子平滑移动到目标位置
   * • 就像变形金刚的变形过程，每个零件都精确归位
   *
   * 🔬 【数学原理】
   * • Cubic easing: 1-(1-t)³ 提供先快后慢的自然运动
   * • 线性插值：current + (target - current) * eased
   * • 阻尼系数0.1：防止粒子移动过快，增加平滑感
   * • 时间归一化：elapsed/duration 映射到[0,1]进度
   *
   * 🎨 【动画特性】
   * • 2秒持续时间：既不会太快也不会太慢
   * • easeOut曲线：开始快，结束慢，符合物理直觉
   * • 帧率自适应：使用requestAnimationFrame同步屏幕刷新
   * • 完成检测：progress达到1时自动进入渐隐阶段
   *
   * @param targetPositions - 🎯 目标位置数组（每个粒子的终点坐标）
   * @private
   */
  private animateToShape(targetPositions: Float32Array): void {
    // 📊 获取当前粒子位置数据引用
    const positions = this.particleGeometry.attributes.position
      .array as Float32Array;

    // ⏱️ 动画参数配置
    const duration = 2000; // 🕐 动画持续时间（2秒）
    const startTime = Date.now(); // 📅 记录动画开始时间戳

    // 🎬 递归动画函数（每帧调用一次）
    const animateShape = () => {
      // ⏱️ 计算已过去的时间和动画进度
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1); // 🔒 进度钳制到[0,1]

      // 📈 应用缓动函数 - easeOut cubic（先快后慢）
      const eased = 1 - Math.pow(1 - progress, 3);

      // 🔄 更新每个粒子位置（线性插值到目标位置）
      for (let i = 0; i < positions.length; i++) {
        // 📐 线性插值公式：当前位置 + (目标位置 - 当前位置) * 插值因子
        positions[i] =
          positions[i] + (targetPositions[i] - positions[i]) * eased * 0.1;
        //                                                              ↑
        //                                                          阻尼系数
        //                                                      (防止移动过快)
      }

      // 🔄 标记几何体需要更新（通知GPU重新上传数据）
      this.particleGeometry.attributes.position.needsUpdate = true;

      // 🔁 继续动画或进入下一阶段
      if (progress < 1) {
        // 🎬 动画未完成，继续下一帧
        requestAnimationFrame(animateShape);
      } else {
        // 🏁 动画完成，开始渐隐效果
        this.fadeOut();
      }
    };

    // 🚀 启动动画循环
    animateShape();
  }

  /**
   * 🌫️ 渐隐效果 - 优雅的谢幕表演
   *
   * 🎭 【比喻】就像舞台剧的落幕时刻：
   * • 聚光灯逐渐调暗
   * • 演员在观众的掌声中缓缓消失
   * • 为整场粒子魔法秀画下完美句号
   *
   * 🔬 【技术实现】
   * • 通过调节材质opacity实现透明度过渡
   * • 1秒渐隐时间提供优雅的结束体验
   * • 完成后自动销毁资源并触发回调
   * • 线性透明度变化：startOpacity * (1 - progress)
   *
   * 🎨 【视觉效果】
   * • 平滑的透明度过渡，无突兀感
   * • 保持粒子形状直到完全消失
   * • 为下一个场景的出现让路
   *
   * @private
   */
  private fadeOut(): void {
    const duration = 1000; // ⏱️ 渐隐持续时间（1秒）
    const startTime = Date.now(); // 📅 开始时间戳
    const startOpacity = this.particleMaterial.opacity; // 💫 初始透明度

    // 🌫️ 渐隐动画函数
    const fade = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // 📉 线性降低透明度：从startOpacity到0
      this.particleMaterial.opacity = startOpacity * (1 - progress);

      if (progress < 1) {
        // 🔄 继续渐隐动画
        requestAnimationFrame(fade);
      } else {
        // 🏁 渐隐完成，清理资源并触发回调
        this.destroy();
        if (this.onCompleteCallback) {
          this.onCompleteCallback(); // 🔔 通知调用者动画完成
        }
      }
    };

    fade(); // 🚀 启动渐隐动画
  }

  /**
   * 🎬 动画循环 - 粒子宇宙的心跳
   *
   * 🎭 【比喻】就像电影院的胶片放映机：
   * • 每秒60次（或显示器刷新率）更新画面
   * • 持续播放直到电影结束（isComplete=true）
   * • 每一帧都让粒子世界"活"起来
   *
   * 🔬 【动画逻辑】
   * 1. **粒子运动**：根据速度更新位置（物理模拟）
   * 2. **系统旋转**：整个粒子群缓慢旋转增加动感
   * 3. **场景渲染**：将3D场景转换为2D图像
   * 4. **循环控制**：检查完成状态决定是否继续
   *
   * 🎨 **视觉效果细节**：
   * • Y轴旋转：0.002弧度/帧 ≈ 每10秒一圈
   * • X轴旋转：0.001弧度/帧 ≈ 每20秒一圈
   * • 双轴旋转创造3D深度感和层次感
   *
   * ⚡ **性能优化**：
   * • 使用requestAnimationFrame同步屏幕刷新
   * • 完成后立即停止循环避免资源浪费
   * • needsUpdate只在位置真正改变时触发
   *
   * @private
   */
  private animate(): void {
    // 🛑 取消之前的动画循环（防止重复启动）
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }

    // 🎬 核心动画循环函数
    const animateLoop = () => {
      // 📋 注册下一帧回调（与屏幕刷新率同步）
      this.animationId = requestAnimationFrame(animateLoop);

      // 🎯 只在未完成状态下执行粒子运动
      if (!this.isComplete) {
        // 📊 获取位置和速度数据引用
        const positions = this.particleGeometry.attributes.position
          .array as Float32Array;
        const velocities = this.particleGeometry.attributes.velocity
          .array as Float32Array;

        // 🚀 更新每个粒子位置（简单物理模拟：位置 += 速度）
        for (let i = 0; i < positions.length; i += 3) {
          positions[i] += velocities[i]; // x坐标更新
          positions[i + 1] += velocities[i + 1]; // y坐标更新
          positions[i + 2] += velocities[i + 2]; // z坐标更新
        }

        // 🔄 标记几何体位置需要更新
        this.particleGeometry.attributes.position.needsUpdate = true;

        // 🌀 粒子系统整体旋转（增加视觉动感）
        this.particles.rotation.y += 0.002; // 🔄 Y轴慢速旋转（水平）
        this.particles.rotation.x += 0.001; // 🔄 X轴超慢旋转（垂直）
      }

      // 🎨 渲染当前帧到屏幕
      this.renderer.render(this.scene, this.camera);
    };

    // 🚀 启动动画循环
    animateLoop();
  }

  /**
   * 🗑️ 销毁加载器 - 释放资源，清理战场
   *
   * 🎭 【比喻】就像演出结束后拆除舞台：
   * • 关闭所有设备（取消动画）
   * • 拆除布景道具（移除DOM元素）
   * • 清理后台资源（释放GPU内存）
   * • 避免内存泄漏这个"幽灵"
   *
   * 🔬 【资源管理】
   * • 取消动画循环防止持续占用CPU
   * • 移除DOM元素避免页面残留
   * • 调用dispose()释放GPU内存
   * • 清空引用帮助垃圾回收
   *
   * ⚠️ **重要性**：
   * • WebGL资源不会自动释放
   * • 不正确清理会导致内存泄漏
   * • 特别在单页应用中容易累积
   *
   * @public
   */
  public destroy(): void {
    // 🛑 停止动画循环
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // 🗑️ 从DOM中移除渲染画布
    if (this.renderer && this.renderer.domElement.parentNode) {
      this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
    }

    // 💾 释放WebGL资源（防止GPU内存泄漏）
    this.renderer?.dispose(); // 释放渲染器资源
    this.particleGeometry?.dispose(); // 释放几何体显存
    this.particleMaterial?.dispose(); // 释放材质纹理
  }

  /**
   * 📱 处理窗口大小变化 - 响应式适配
   *
   * 🎭 【比喻】就像自动调节的投影屏幕：
   * • 窗口变大时画面也变大
   * • 窗口变小时画面也变小
   * • 始终保持完美的宽高比
   *
   * 🔬 【技术实现】
   * • 重新获取容器的实际尺寸
   * • 更新相机的宽高比和投影矩阵
   * • 重设渲染器的输出尺寸
   * • 确保画面不变形、不裁剪
   *
   * 📱 **响应式设计**：
   * • 支持移动端旋转屏幕
   * • 适配各种屏幕尺寸
   * • 保持最佳视觉效果
   *
   * @public
   */
  public resize(): void {
    // 📏 获取容器当前尺寸
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    // 📷 更新相机参数（如果是透视相机）
    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = width / height; // 🔄 更新宽高比
      this.camera.updateProjectionMatrix(); // 🔄 重新计算投影矩阵
    }

    // 🖥️ 更新渲染器输出尺寸
    this.renderer.setSize(width, height);
  }
}

/**
 * 🎉 ParticleLoader 使用总结
 * ========================================
 *
 * 🌟 **核心功能**：
 * • ✨ 科幻风格的粒子加载动画
 * • 📊 实时进度反馈和视觉响应
 * • 🛣️ 最终汇聚成3D道路形状
 * • 🌫️ 优雅的渐隐结束效果
 *
 * 🎨 **视觉特性**：
 * • 🌌 数千粒子模拟星辰汇聚
 * • ✨ 发光混合效果增强科技感
 * • 🌀 3D旋转增加空间层次感
 * • 📱 完全响应式设计
 *
 * ⚡ **性能优化**：
 * • 🚀 BufferGeometry批量处理顶点
 * • 🎯 requestAnimationFrame同步刷新率
 * • 💾 完善的资源管理和释放机制
 * • 📊 高效的数学运算和内存操作
 *
 * 🔧 **技术亮点**：
 * • 🎲 球面均匀分布算法
 * • 📐 向心速度物理模拟
 * • 🎬 三段式动画流程设计
 * • 🔄 平滑的形状变换插值
 *
 * 💡 **应用建议**：
 * • 适合V2X交通可视化系统的入场动画
 * • 可扩展为各种3D模型的粒子重组效果
 * • 建议配合loading文字或进度条使用
 * • 在移动端可适当减少粒子数量优化性能
 */
